import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { TranslateService } from '@ngx-translate/core';
import { AgGridSliderRangeFilterComponent } from './ag-grid-slider-range-filter/ag-grid-slider-range-filter.component';
import { AG_GRID_LOCALE_FR } from './ag-grid-translations/locale.fr';
import { AG_GRID_LOCALE_EN } from './ag-grid-translations/locale.en';
import {BehaviorSubject, combineLatest, Subject, Subscription} from 'rxjs';
import { ReportUtils } from '../../libraries/report-utils';
import { AgGridDateFilterComponent } from './ag-grid-date-filter/ag-grid-date-filter.component';
import {
  AllModules,
  ColumnEverythingChangedEvent,
  ColumnResizedEvent,
  GetContextMenuItems,
  IServerSideDatasource
} from '@ag-grid-enterprise/all-modules';
import { AgGridHeaderGroupComponent } from './ag-grid-header-group/ag-grid-header-group.component';
import { FormControl } from '@angular/forms';
import {AgGridEvent, ColDef, ColumnPinnedEvent} from '@ag-grid-community/core';
import { AgGridHeaderGroupIconComponent } from './ag-grid-header-group-icon/ag-grid-header-group-icon.component';
import { AgGridOverlayLoadingComponent } from './ag-grid-overlay-loading/ag-grid-overlay-loading.component';
import { AgGridOverlayNoRowsComponent } from './ag-grid-overlay-no-rows/ag-grid-overlay-no-rows.component';
import { SelectOption, SelectOptions } from '../../interfaces/form';
import {ActivatedRoute, Router} from '@angular/router';
import { AgGridCustomTooltipComponent } from "./ag-grid-custom-tooltip/ag-grid-custom-tooltip.component";
import {
  AgGridConversionPathFilterComponent
} from "./ag-grid-conversion-path-filter/ag-grid-conversion-path-filter.component";
import { Lang } from "../../interfaces/ad-reports";
import {delay, filter, first, startWith, tap} from "rxjs/operators";
import {FullscreenService} from "../../services/fullscreen.service";
import {LayoutService} from "../../services/layout.service";
import {enterLeaveTableOpacityAnimation} from '../../animations/show-opacity.animation';

@Component({
  selector: 'app-ag-grid',
  templateUrl: './ag-grid.component.html',
  styleUrls: ['./ag-grid.component.scss'],
  animations: [enterLeaveTableOpacityAnimation]
})
export class AgGridComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('agGrid', { read: ElementRef }) private readonly agGridE: ElementRef;
  @ViewChild('agGrid', { read: AgGridAngular }) public readonly agGrid: AgGridAngular;

  @Input('pagination') public pagination: boolean = true;
  @Input('rowCount') public rowCount: number;
  @Input('enablePageSizeSelect') public enablePageSizeSelect: boolean;

  public paginationPageSize$: BehaviorSubject<number> = new BehaviorSubject(null);

  @Input('paginationPageSize') public set inputSetPaginationPageSize(pageSize: number) {
    if (pageSize) {
      this.paginationPageSize$.next(pageSize);

      const sizeOption: number | string = this.nbRowsOptions.find((option: SelectOption<number | string, string>): boolean => option.key === pageSize) && pageSize || 'all';
      this.nbRowsCtrl.setValue(sizeOption, { emitEvent: false });
    }
  }

  public rowData: Array<any>;

  @Input('rowData') set setRowData(rowData: Array<any>) {
    this.rowData = rowData;
  }
  @Input('rowBuffer') public rowBuffer: number;
  @Input('defaultColDef') public defaultColDef: any;
  @Input('pinnedBottomRowData') public pinnedBottomRowData: Array<any>;
  @Input('domLayout') public domLayout: string;
  @Input('loadingOverlayComponentParams') public loadingOverlayComponentParams: any;
  @Input('noRowsOverlayComponentParams') public noRowsOverlayComponentParams: any;
  @Input('loadingOverlayComponent') public loadingOverlayComponent: string;
  @Input('noRowsOverlayComponent') public noRowsOverlayComponent: string;
  @Input('overlayNoRowsTemplate') public overlayNoRowsTemplate: string;
  @Input('overlayLoadingTemplate') public overlayLoadingTemplate: string;
  @Input('resetOnLangChanges') public resetOnLangChanges: boolean;
  @Input('filterModel') public filterModel: any;
  @Input('canFullscreen') public canFullscreen: boolean;
  @Input('clipboardDeliminator') public clipboardDeliminator: string;
  @Input('suppressContextMenu') public suppressContextMenu: boolean;
  @Input('processCellForClipboard') public processCellForClipboard: Function;
  @Input('enableRangeSelection') public enableRangeSelection: boolean;
  @Input('getContextMenuItems') public getContextMenuItems: GetContextMenuItems | undefined;
  @Input('rowModelType') public rowModelType: 'serverSide' | 'infinite';
  @Input('serverSideStoreType') public serverSideStoreType: 'partial';

  @Input('gridOptions') public gridOptions: any = { suppressDragLeaveHidesColumns: true };
  @Input('isExternalFilterPresent') public isExternalFilterPresent: Function;
  @Input('doesExternalFilterPass') public doesExternalFilterPass: Function;
  @Input('suppressSizeColumnToFit') public suppressSizeColumnToFit: boolean = false;
  @Input('nbRowsOptions') public nbRowsOptions: SelectOptions<string | number, string> = [
    {key: 10, text: '10'},
    {key: 25, text: '25'},
    {key: 50, text: '50'},
    {key: 100, text: '100'},
    {key: 'all', text: 'reports.all'}
  ];

  @Input() public serverSideDataSource: IServerSideDatasource;

  @Input('loader') public set _setLoader(loader: boolean) {
    this.loader = loader;
    this._showLoader();
  };
  @Input('columnDefs') public  set _columnDefs(columnDefs: Array<ColDef>) {
    this.columnDefs = columnDefs || [];
  }

  @Output('gridReady') public gridReadyE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('rowClicked') public rowClickedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('firstDataRendered') public firstDataRenderedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('paginationChanged') public paginationChangedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('columnMoved') public columnMovedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('dragStopped') public dragStoppedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('sortChanged') public sortChangedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('filterChanged') public filterChangedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('rowDataChanged') public rowDataChangedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('pageSizeChanged') public pageSizeChangedE: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @Output('paginationPageSizedChanged') public paginationPageSizedChangedE: EventEmitter<number> = new EventEmitter<number>();
  @Output('columnResized') public columnResizedE: EventEmitter<ColumnResizedEvent> = new EventEmitter<ColumnResizedEvent>();
  @Output('columnPinned') public columnPinnedE: EventEmitter<ColumnPinnedEvent> = new EventEmitter<ColumnPinnedEvent>();
  @Output('columnEverythingChanged') public columnEverythingChangedE: EventEmitter<ColumnEverythingChangedEvent> = new EventEmitter<ColumnEverythingChangedEvent>();

  public readonly frameworksComponents: any = {
    agSliderRangeColumnFilter: AgGridSliderRangeFilterComponent,
    agCustomDateColumnFilter: AgGridDateFilterComponent,
    customHeaderGroupComponent: AgGridHeaderGroupComponent,
    customHeaderGroupIconComponent: AgGridHeaderGroupIconComponent,
    customOverlayLoading: AgGridOverlayLoadingComponent,
    customTooltip: AgGridCustomTooltipComponent,
    customOverlayNoRows: AgGridOverlayNoRowsComponent,
    agConversionPathFilter: AgGridConversionPathFilterComponent
  };

  public readonly rowClassRules: any = {};
  public localeText: any = null;
  public params: AgGridEvent;
  public modules: Array<any> = AllModules;
  public loader: boolean = false;
  public resizeLoader: boolean = false;
  public columnDefs: Array<any> = [];

  public readonly control: FormControl = new FormControl();
  public readonly nbRowsCtrl: FormControl = new FormControl();

  private onTranslateChangeSubs:  Subscription;
  private nbRowsSubs:             Subscription;
  private queryParamsSubs:        Subscription;
  private resizeSubs:             Subscription;

  private readonly _resize$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _translateS: TranslateService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _fullscreenS: FullscreenService,
    private readonly _layoutS: LayoutService
  ) {}

  ngOnInit(): void {
    this.resizeSubs = combineLatest([
      this._resize$,
      this._layoutS.onSidenav$,
      this._fullscreenS.onFullscreen$
    ]).pipe(
      tap(() => {
        if (!this.suppressSizeColumnToFit) {
          this.resizeLoader = true;
        }
      }),
      delay(500)
    )
    .subscribe(() => {
      this.sizeColumnsToFit();
      this.resizeLoader = false;
    });
  }

  ngAfterViewInit(): void {
    this.columnDefs = [...this.columnDefs];

    this._resize$.next();

    this.onTranslateChangeSubs = this._translateS.onLangChange
      .pipe(
        startWith({lang: this._translateS.defaultLang}),
        tap((lang) => {
          this._setLang(lang.lang as Lang);
        })
      )
      .subscribe();

    this.nbRowsSubs = this.nbRowsCtrl.valueChanges
      .subscribe((size: number | string) => {
        const pageSize: number = (size !== 'all' && size || this.rowCount || this.rowData && this.rowData.length || 1000000) as number;

        if (this.rowModelType === 'serverSide') {
          this.gridOptions.cacheBlockSize = pageSize;
          this.gridOptions.paginationPageSize = pageSize;
        } else {
          this.params.api.paginationSetPageSize(pageSize);
        }

        this.paginationPageSizedChangedE.emit(pageSize);
      });
  }

  ngOnDestroy(): void {
    ReportUtils.unsubscribe(this.onTranslateChangeSubs);
    ReportUtils.unsubscribe(this.nbRowsSubs);
    ReportUtils.unsubscribe(this.queryParamsSubs);
    ReportUtils.unsubscribe(this.resizeSubs);

    this.agGrid.api.destroy();
  }

  private _setLang(lang: Lang): void {
    this.translations(lang);
    this.tableRefresh();
  }

  private _showLoader(): void {
    if (this.agGrid) {
      if (this.loader) {
        this.agGrid.api.showLoadingOverlay();
      } else {
        this.agGrid.api.hideOverlay();
      }
    }
  }

  private translations(lang: Lang): void {
    switch (lang) {
      case 'en':
        this.localeText = AG_GRID_LOCALE_EN;
        break;
      case 'fr':
        this.localeText = AG_GRID_LOCALE_FR;
        break;
    }
  }

  private tableRefresh(): void {
    if (this.agGrid) {
      this.agGrid.api.refreshHeader();
      this.agGrid.api.refreshToolPanel();
    }
  }

  public onGridReady(params: AgGridEvent): void {
    this.params = params;
    this.gridReadyE.emit(params);

    this.sizeColumnsToFit(params);

    this.paginationPageSize$.pipe(
      filter(value => value !== null),
      first()
    ).subscribe(size => {
      this.nbRowsCtrl.setValue(size);
    });
  }

  public onFirstDataRendered(params: AgGridEvent): void {
    this.sizeColumnsToFit(params);
    this.firstDataRenderedE.emit(params);
  }

  public onModelUpdated(params: AgGridEvent): void {
  }

  public onFilterChanged(params: AgGridEvent): void {
    this._router.navigate([], {
      relativeTo: this._route,
      replaceUrl: false
    });
  }

  public columnEverythingChanged(params: AgGridEvent & { source: string }): void {
    if (params.type === 'columnEverythingChanged' && params.source === 'gridOptionsChanged') {
      this.tableRefresh();
    }

    this.columnEverythingChangedE.emit(params);
  }

  public rowDataChanged(params: AgGridEvent): void {
    params.api.resetRowHeights();
    setTimeout(() => {
      this.sizeColumnsToFit(params);
    }, 250);
    this.rowDataChangedE.emit(params);
  }

  public sizeColumnsToFit(agGrid: AgGridEvent | AgGridAngular = this.agGrid): void {
    if (!this.suppressSizeColumnToFit && agGrid) {
      agGrid.columnApi.sizeColumnsToFit(this.agGridE.nativeElement.clientWidth - 17);
    }
  }

  public onPaginationChanged(params: AgGridEvent): void {
    this.paginationChangedE.emit(params);
  }

  public nbRowsValueGetter(option: SelectOption<any, any>): string | number {
    return option.key;
  }

  @HostListener('window:resize', ['$event']) private _resize(): void {
    this.sizeColumnsToFit();
  }

}
